import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./About.css";
import logo from "../assets/logo.jpg";
import SEOHead from "./SEOHead";

const About = () => {
  const navigate = useNavigate();

  const pillars = [
    {
      icon: "🏠",
      title: "Housing",
      subtitle: "Find & Compare",
      desc: "Culturally-matched properties, compatible roommates, and neighbourhood intelligence — filtered by lifestyle and values, not just price.",
      color: "#1E88E5",
      bg: "rgba(30,136,229,0.07)",
    },
    {
      icon: "🍽️",
      title: "Food",
      subtitle: "Discover & Explore",
      desc: "Restaurants, cafés, and markets matched to your cuisine preferences — wherever you are in the world.",
      color: "#FF9800",
      bg: "rgba(255,152,0,0.07)",
    },
    {
      icon: "👥",
      title: "Community",
      subtitle: "Connect & Ask",
      desc: "Local Q&A and tips across Housing, Food, Transit, Safety, Nightlife, Pets, and Events — connect with locals and expats who know.",
      color: "#4CAF50",
      bg: "rgba(76,175,80,0.07)",
    },
    {
      icon: "📰",
      title: "Feed",
      subtitle: "Stay Informed",
      desc: "Local and global news, community events, popular city guides, and social posts — personalised to your city and interests.",
      color: "#9C27B0",
      bg: "rgba(156,39,176,0.07)",
    },
  ];

  const values = [
    { icon: "🤝", title: "Community First", desc: "Every feature is designed to foster real connections between people who share values, interests, and cultural backgrounds." },
    { icon: "🌍", title: "Cultural Inclusivity", desc: "We celebrate diversity and ensure everyone — regardless of background — finds a community where they feel understood." },
    { icon: "🔒", title: "Trust & Safety", desc: "Verified profiles, community ratings, and authenticity checks keep the platform safe for everyone." },
    { icon: "💡", title: "Beyond Housing", desc: "We connect you to local restaurants, events, community groups, and services — not just a lease." },
  ];

  return (
    <div className="about-page">
      <SEOHead
        title="About ComLinkr — Community-First Housing, Food & Lifestyle Platform"
        description="Learn what ComLinkr is — a platform connecting people to culturally-matched housing, local food, community, and a personalised feed. Built for movers, students, and explorers."
        canonical="https://www.comlinkr.com/about"
      />

      {/* Back button */}
      <button className="back-btn" onClick={() => navigate("/")}>← Back to Home</button>

      {/* Hero */}
      <section className="about-hero">
        <div className="container text-center">
          <motion.img
            src={logo}
            alt="ComLinkr"
            className="about-logo"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, type: "spring" }}
          />
          <motion.h1
            className="about-hero-title"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            About <span className="brand-highlight">ComLinkr</span>
          </motion.h1>
          <motion.p
            className="about-hero-sub"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            ComLinkr is a community-first platform that connects people to housing, food, and local communities
            matched to their culture and lifestyle — so you feel at home, not just housed.
          </motion.p>
        </div>
      </section>

      {/* What we are */}
      <section className="about-what">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <motion.div
                initial={{ x: -40, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
              >
                <span className="page-eyebrow">Our Idea</span>
                <h2 className="page-section-title">More than an app — a way to belong</h2>
                <p className="page-lead">
                  Moving to a new city — or even exploring your own — should feel exciting, not isolating.
                  ComLinkr was built because finding a home was never just about rent and square footage.
                  It's about finding your people, your culture, and your community.
                </p>
                <p className="page-lead">
                  Whether you're an international mover, a graduate student, a remote worker, or a local
                  explorer — ComLinkr helps you build a life, not just find a lease.
                </p>
              </motion.div>
            </div>
            <div className="col-lg-6">
              <div className="row g-3">
                {pillars.map((p, i) => (
                  <div key={i} className="col-6">
                    <motion.div
                      className="pillar-mini"
                      style={{ "--pc": p.color, "--pb": p.bg }}
                      initial={{ y: 30, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                    >
                      <span className="pillar-mini-icon">{p.icon}</span>
                      <span className="pillar-mini-title" style={{ color: p.color }}>{p.title}</span>
                      <span className="pillar-mini-sub">{p.subtitle}</span>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Pillars detail */}
      <section className="about-pillars">
        <div className="container">
          <div className="text-center mb-5">
            <span className="page-eyebrow">What We Offer</span>
            <h2 className="page-section-title">Four pillars. One platform.</h2>
          </div>
          <div className="row g-4">
            {pillars.map((p, i) => (
              <div key={i} className="col-lg-6">
                <motion.div
                  className="pillar-detail-card"
                  style={{ "--pc": p.color, "--pb": p.bg }}
                  initial={{ y: 40, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="pillar-detail-icon">{p.icon}</div>
                  <div>
                    <h4 className="pillar-detail-title">{p.title}</h4>
                    <span className="pillar-detail-sub">{p.subtitle}</span>
                    <p className="pillar-detail-desc">{p.desc}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about-values">
        <div className="container">
          <div className="text-center mb-5">
            <span className="page-eyebrow">What We Stand For</span>
            <h2 className="page-section-title">Our Core Values</h2>
          </div>
          <div className="row g-4">
            {values.map((v, i) => (
              <div key={i} className="col-lg-3 col-md-6">
                <motion.div
                  className="value-card"
                  initial={{ y: 40, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -6 }}
                >
                  <div className="value-icon">{v.icon}</div>
                  <h5 className="value-title">{v.title}</h5>
                  <p className="value-desc">{v.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Light CTA */}
      <section className="about-light-cta">
        <div className="container text-center">
          <span className="page-eyebrow">Get Involved</span>
          <h2 className="page-section-title">Ready to find your community?</h2>
          <p className="page-lead" style={{ maxWidth: 520, margin: "0 auto 2rem" }}>
            Join thousands of early members shaping the future of how people connect to housing, food, and community.
          </p>
          <button className="btn-primary-page" onClick={() => navigate("/team")}>Meet the Team</button>
          <button className="btn-outline-page ms-3" onClick={() => navigate("/mission")}>Our Mission</button>
        </div>
      </section>

    </div>
  );
};

export default About;
